import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import {
  FaSearch, FaUser, FaIdCard, FaPhone, FaEnvelope,
  FaFileAlt, FaUserTie, FaCar, FaEdit, FaSave, FaTimes,
  FaPlus, FaCheckCircle, FaTimesCircle, FaSpinner,
  FaMapMarkerAlt, FaHome, FaCity, FaMapPin, FaRoad,
  FaGlobeAmericas, FaBuilding, FaCheck, FaExclamationTriangle
} from 'react-icons/fa';
import { MdRequestPage } from 'react-icons/md';
import axios from 'axios';
import InputMask from 'react-input-mask';
import { NumericFormat } from 'react-number-format';
import { useNavigate } from 'react-router-dom';
import config from '../config/config';
import { useToast } from '../components/ToastContext';
import { consultarCnpj } from '../others/consultaCnpj';
import ImportarDocumentoVeiculo from './ImportarDocumentoVeiculo';
import ImportarPrintCliente, { ESTILO_CAMPO_DO_PRINT, cepVeioDoPrint } from './ImportarPrintCliente';
import { buscarVeiculo, salvarVeiculo } from '../others/cadastroVeiculo';

// ─── Modal de Edição de Cliente ───────────────────────────────────────────────

const EditClienteModal = ({ cliente, onSave, onClose, API_BASE_URL }) => {
  const { addToast } = useToast();

  const [form, setForm] = useState({
    nome:       cliente.nome        || cliente.razaoSocial || '',
    cpf:        cliente.cpf         || '',
    cnpj:       cliente.cnpj        || '',
    identidade: cliente.identidade  || '',
    telefone:   cliente.telefone    || '',
    email:      cliente.email       || '',
    logradouro: cliente.logradouro  || '',
    numero:     cliente.numero      || '',
    bairro:     cliente.bairro      || '',
    // ── CORREÇÃO: campo Complemento estava faltando neste modal de edição —
    // já existia no cadastro original de clientes e no formulário de Novo
    // Cliente (NovoClienteInline) logo abaixo, mas "sumia" ao editar um
    // cliente já existente. ──
    complemento: cliente.complemento || '',
    cidade:     cliente.cidade      || '',
    uf:         cliente.uf          || '',
    cep:        cliente.cep         || '',
  });
  const [saving, setSaving] = useState(false);
  // ─── CORREÇÃO: estado de loading da busca de CEP ───────────────────────────
  const [loadingCep, setLoadingCep] = useState(false);
  // ─── CORREÇÃO: este modal de edição não validava CPF/CNPJ antes de salvar
  // — diferente do formulário de "Novo Cliente" (NovoClienteInline), que já
  // validava. Isso deixava passar documentos mal formados (ex: CPF com um
  // dígito a mais) direto pro banco, sem nenhum aviso. Depois, ajustamos de
  // novo: a checagem por dígito verificador (checksum) estava bloqueando
  // documentos legítimos e antigos que tinham a quantidade certa de dígitos
  // mas falhavam no cálculo matemático (ex: um erro de digitação de anos
  // atrás, já em uso normalmente pelo cliente). Agora a regra é a mesma da
  // tela de Clientes: só confere a QUANTIDADE de dígitos (11/14) e se já
  // existe outro cliente com o mesmo documento — sem checar o dígito
  // verificador. ───────────────────────────────────────────────────────────
  const [errors, setErrors] = useState({});

  const handle = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  // Print colado (Ctrl+V) com os dados do cliente: sobrescreve os campos lidos e destaca em amarelo os que mudaram.
  // Nada é salvo sozinho. O destaque some ao fechar o formulário (o componente é desmontado).
  const [camposDoPrint, setCamposDoPrint] = useState([]);
  // CEP preenchido pelo print: enquanto não mudar, sair do campo CEP não busca o endereço na API
  const [cepDoPrint, setCepDoPrint] = useState('');
  const aplicarPrint = (dados, alterados) => {
    setForm(prev => ({ ...prev, ...dados }));
    setCamposDoPrint(prev => [...new Set([...prev, ...alterados])]);
    if (dados.cep) setCepDoPrint(dados.cep);
    setErrors(prev => { const e = { ...prev }; Object.keys(dados).forEach(campo => delete e[campo]); return e; });
  };
  const destaquePrint = (campo) => (camposDoPrint.includes(campo) ? ESTILO_CAMPO_DO_PRINT : {});

  const validar = () => {
    const e = {};
    if (!form.nome.trim() || form.nome.trim().length < 3) e.nome = 'Nome deve ter pelo menos 3 caracteres';
    const cpfDigitos = form.cpf ? form.cpf.replace(/\D/g, '') : '';
    const cnpjDigitos = form.cnpj ? form.cnpj.replace(/\D/g, '') : '';
    if (cpfDigitos && cpfDigitos.length !== 11) e.cpf = 'CPF deve ter 11 dígitos';
    if (cnpjDigitos && cnpjDigitos.length !== 14) e.cnpj = 'CNPJ deve ter 14 dígitos';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ─── CORREÇÃO: busca automática de endereço via ViaCEP ─────────────────────
  // Esse comportamento já existia no formulário de "Novo Cliente" (NovoClienteInline),
  // mas faltava aqui no modal de EDIÇÃO de cliente existente, que é o que a tela de
  // Requerimento usa quando o usuário clica em "editar dados" após selecionar um cliente.
  const buscarCep = async (cepValue) => {
    const c = (cepValue || '').replace(/\D/g, '');
    if (c.length !== 8) return;
    setLoadingCep(true);
    try {
      const res = await axios.get(`https://viacep.com.br/ws/${c}/json/`);
      if (res.data && !res.data.erro) {
        setForm((prev) => ({
          ...prev,
          logradouro: res.data.logradouro || prev.logradouro,
          bairro:     res.data.bairro     || prev.bairro,
          cidade:     res.data.localidade || prev.cidade,
          uf:         res.data.uf         || prev.uf,
        }));
        addToast('success', 'Endereço preenchido pelo CEP!');
      } else {
        addToast('warning', 'CEP não encontrado');
      }
    } catch {
      addToast('error', 'Erro ao buscar CEP');
    } finally {
      setLoadingCep(false);
    }
  };

  const handleSave = async () => {
    if (!validar()) {
      addToast('warning', 'Corrija os erros antes de salvar');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('jwtToken');
      const id = cliente.id || cliente._id || cliente.clienteId || cliente.idCliente || cliente.codigo || cliente.cd_cliente;

      // ── NOVO: checagem de duplicidade — mesma regra da tela de Clientes.
      // Só bloqueia se o CPF/CNPJ já pertencer a OUTRO cliente. ──
      const cpfDigitos = form.cpf ? form.cpf.replace(/\D/g, '') : '';
      const cnpjDigitos = form.cnpj ? form.cnpj.replace(/\D/g, '') : '';
      try {
        if (cpfDigitos.length === 11) {
          const res = await axios.get(`${API_BASE_URL}/clientes/cpf/${cpfDigitos}`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.data && res.data.idCliente !== id) {
            setErrors(prev => ({ ...prev, cpf: 'CPF já cadastrado para outro cliente' }));
            addToast('warning', 'Este CPF já está cadastrado para outro cliente');
            setSaving(false);
            return;
          }
        }
        if (cnpjDigitos.length === 14) {
          const res = await axios.get(`${API_BASE_URL}/clientes/cnpj/${cnpjDigitos}`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.data && res.data.idCliente !== id) {
            setErrors(prev => ({ ...prev, cnpj: 'CNPJ já cadastrado para outro cliente' }));
            addToast('warning', 'Este CNPJ já está cadastrado para outro cliente');
            setSaving(false);
            return;
          }
        }
      } catch {
        // 404 = não encontrado = não é duplicado, segue normalmente
      }

      const payload = {
        nome:       form.nome,
        cpf:        form.cpf        ? form.cpf.replace(/\D/g, '')      : undefined,
        cnpj:       form.cnpj       ? form.cnpj.replace(/\D/g, '')     : undefined,
        identidade: form.identidade || undefined,
        telefone:   form.telefone   ? form.telefone.replace(/\D/g, '') : undefined,
        email:      form.email      || undefined,
        logradouro: form.logradouro || undefined,
        numero:     form.numero     || undefined,
        bairro:     form.bairro     || undefined,
        complemento: form.complemento || undefined,
        cidade:     form.cidade     || undefined,
        uf:         form.uf         || undefined,
        cep:        form.cep        ? form.cep.replace(/\D/g, '')      : undefined,
      };
      Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);

      const clienteAtualizado = { ...cliente, ...form };

      try {
        const response = await axios.put(
          `${API_BASE_URL}/clientes/${id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data && Object.keys(response.data).length > 0) {
          Object.assign(clienteAtualizado, response.data);
        }
        addToast('success', 'Dados salvos e aplicados com sucesso!');
      } catch (apiErr) {
        const detail =
          apiErr.response?.data?.message ||
          apiErr.response?.data?.error   ||
          apiErr.response?.data?.details ||
          (typeof apiErr.response?.data === 'string' ? apiErr.response.data : null) ||
          apiErr.message;
        addToast('warning', `Não foi possível salvar no servidor (${detail}), mas os dados foram aplicados localmente.`);
      }

      onSave(clienteAtualizado);
    } catch (err) {
      addToast('error', 'Erro inesperado: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <EditOverlay onClick={onClose}>
      <EditContainer onClick={(e) => e.stopPropagation()}>
        <EditHeader>
          <FaEdit size={18} />
          <span>Editar Dados do Cliente</span>
          <EditCloseBtn onClick={onClose}><FaTimes /></EditCloseBtn>
        </EditHeader>

        <EditBody>
          <ImportarPrintCliente valoresAtuais={form} onDadosLidos={aplicarPrint} disabled={saving} />
          <EditRow>
            <EditGroup full>
              <EditLabel>Nome / Razão Social</EditLabel>
              <EditInput value={form.nome} style={destaquePrint('nome')} onChange={handle('nome')} />
              {errors.nome && <NCError>{errors.nome}</NCError>}
            </EditGroup>
          </EditRow>

          {/* ── CORREÇÃO: CPF e CNPJ agora aparecem SEMPRE juntos para edição,
              independente de qual o cliente já tinha preenchido. Isso permite
              completar o cadastro de um cliente PF que também precise de CNPJ
              (ou vice-versa), e resolve o bug onde o campo CNPJ nunca aparecia. ── */}
          <EditRow>
            <EditGroup>
              <EditLabel>CPF</EditLabel>
              <EditInput value={form.cpf} style={destaquePrint('cpf')} onChange={handle('cpf')} maxLength={14} placeholder="000.000.000-00" />
              {errors.cpf && <NCError>{errors.cpf}</NCError>}
            </EditGroup>
            <EditGroup>
              <EditLabel>CNPJ</EditLabel>
              <EditInput value={form.cnpj} style={destaquePrint('cnpj')} onChange={handle('cnpj')} maxLength={18} placeholder="00.000.000/0000-00" />
              {errors.cnpj && <NCError>{errors.cnpj}</NCError>}
            </EditGroup>
            <EditGroup>
              <EditLabel>RG / Identidade</EditLabel>
              <EditInput value={form.identidade} onChange={handle('identidade')} maxLength={20} placeholder="Nº do documento" />
            </EditGroup>
          </EditRow>

          <EditRow>
            <EditGroup>
              <EditLabel>Telefone</EditLabel>
              <EditInput value={form.telefone} style={destaquePrint('telefone')} onChange={handle('telefone')} maxLength={15} placeholder="(00) 00000-0000" />
            </EditGroup>
            <EditGroup>
              <EditLabel>E-mail</EditLabel>
              <EditInput value={form.email} style={destaquePrint('email')} onChange={handle('email')} type="email" placeholder="email@exemplo.com" />
            </EditGroup>
          </EditRow>

          <EditSectionLabel>Endereço</EditSectionLabel>

          {/* ── CORREÇÃO: campo CEP movido para o topo do bloco de endereço,
              com onBlur chamando buscarCep, igual ao formulário de Novo Cliente.
              Assim que o usuário sai do campo CEP (ou termina de digitar os 8
              dígitos), o endereço é buscado e os campos abaixo são preenchidos
              automaticamente. ── */}
          <EditRow>
            <EditGroup style={{ maxWidth: 160 }}>
              <EditLabel>CEP</EditLabel>
              <InputMask
                mask="99999-999"
                value={form.cep} style={destaquePrint('cep')}
                onChange={handle('cep')}
                onBlur={(e) => { if (cepVeioDoPrint(e.target.value, cepDoPrint)) return; buscarCep(e.target.value); }}
                placeholder="00000-000"
              >
                {(props) => <EditInput {...props} />}
              </InputMask>
              {loadingCep && <EditHint>Buscando endereço...</EditHint>}
            </EditGroup>
          </EditRow>

          <EditRow>
            <EditGroup full>
              <EditLabel>Logradouro</EditLabel>
              <EditInput value={form.logradouro} style={destaquePrint('logradouro')} onChange={handle('logradouro')} />
            </EditGroup>
          </EditRow>

          <EditRow>
            <EditGroup style={{ maxWidth: 90 }}>
              <EditLabel>Número</EditLabel>
              <EditInput value={form.numero} style={destaquePrint('numero')} onChange={handle('numero')} />
            </EditGroup>
            <EditGroup>
              <EditLabel>Bairro</EditLabel>
              <EditInput value={form.bairro} style={destaquePrint('bairro')} onChange={handle('bairro')} />
            </EditGroup>
            <EditGroup>
              <EditLabel>Cidade</EditLabel>
              <EditInput value={form.cidade} style={destaquePrint('cidade')} onChange={handle('cidade')} />
            </EditGroup>
            <EditGroup style={{ maxWidth: 60 }}>
              <EditLabel>UF</EditLabel>
              <EditInput value={form.uf} style={destaquePrint('uf')} onChange={handle('uf')} maxLength={2} />
            </EditGroup>
          </EditRow>

          {/* ── NOVO: campo Complemento — faltava neste modal de edição,
              embora já existisse no cadastro original de clientes e no
              formulário de Novo Cliente logo abaixo. ── */}
          <EditRow>
            <EditGroup full>
              <EditLabel>Complemento</EditLabel>
              <EditInput value={form.complemento} style={destaquePrint('complemento')} onChange={handle('complemento')} placeholder="Apto, bloco, sala..." />
            </EditGroup>
          </EditRow>
        </EditBody>

        <EditFooter>
          <EditCancelBtn onClick={onClose}>Cancelar</EditCancelBtn>
          <EditSaveBtn onClick={handleSave} disabled={saving}>
            <FaSave size={13} /> {saving ? 'Salvando...' : 'Salvar Alterações'}
          </EditSaveBtn>
        </EditFooter>
      </EditContainer>
    </EditOverlay>
  );
};

// ─── Formulário inline de Novo Cliente ───────────────────────────────────────

const FORM_VAZIO = {
  nome: '', identidade: '', cpf: '', cnpj: '', telefone: '', email: '',
  logradouro: '', numero: '', bairro: '', complemento: '', cep: '', cidade: '', uf: '',
};

const NovoClienteInline = ({ onSalvo, onCancelar, API_BASE_URL }) => {
  const { addToast } = useToast();
  const [form, setForm] = useState(FORM_VAZIO);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [cpfStatus, setCpfStatus]   = useState(null);
  const [cnpjStatus, setCnpjStatus] = useState(null);
  const [cpfMsg, setCpfMsg]         = useState('');
  const [cnpjMsg, setCnpjMsg]       = useState('');
  const [loadingCep, setLoadingCep] = useState(false);
  const cpfTimer  = useRef(null);
  const cnpjTimer = useRef(null);

  const set = (field) => (e) =>
    setForm((p) => ({ ...p, [field]: e.target.value }));

  // Print colado (Ctrl+V) com os dados do cliente: sobrescreve os campos lidos e destaca em amarelo os que mudaram.
  // Nada é salvo sozinho. O destaque some ao fechar o formulário (o componente é desmontado).
  const [camposDoPrint, setCamposDoPrint] = useState([]);
  // CEP preenchido pelo print: enquanto não mudar, sair do campo CEP não busca o endereço na API
  const [cepDoPrint, setCepDoPrint] = useState('');
  const aplicarPrint = (dados, alterados) => {
    setForm(prev => ({ ...prev, ...dados }));
    setCamposDoPrint(prev => [...new Set([...prev, ...alterados])]);
    if (dados.cep) setCepDoPrint(dados.cep);
    setErrors(prev => { const e = { ...prev }; Object.keys(dados).forEach(campo => delete e[campo]); return e; });
    if (dados.cpf) verificarCpf(dados.cpf);
    if (dados.cnpj) verificarCnpj(dados.cnpj);
  };
  const destaquePrint = (campo) => (camposDoPrint.includes(campo) ? ESTILO_CAMPO_DO_PRINT : {});

  /* ── validações ──
     ── CORREÇÃO: a checagem por dígito verificador (checksum) bloqueava
     documentos legítimos e antigos que tinham a quantidade certa de dígitos
     mas falhavam no cálculo matemático. Agora a regra é a mesma da tela de
     Clientes: só confere a QUANTIDADE de dígitos e se já existe outro
     cliente cadastrado com o mesmo documento (consulta real à API, igual
     ao OrdemServico.jsx). ── */
  const verificarCpf = async (val) => {
    const digitos = val.replace(/\D/g, '');
    if (digitos.length !== 11) {
      setCpfStatus('invalid');
      setCpfMsg('CPF deve ter 11 dígitos');
      setErrors(p => ({ ...p, cpf: 'CPF deve ter 11 dígitos' }));
      return;
    }
    setCpfStatus('checking');
    setCpfMsg('Verificando CPF...');
    setErrors(p => { const n = { ...p }; delete n.cpf; return n; });
    try {
      const token = localStorage.getItem('jwtToken');
      const res = await axios.get(`${API_BASE_URL}/clientes/cpf/${digitos}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data) {
        setCpfStatus('duplicate');
        setCpfMsg('CPF já cadastrado');
        setErrors(p => ({ ...p, cpf: 'CPF já cadastrado' }));
      } else {
        setCpfStatus('valid');
        setCpfMsg('CPF válido');
      }
    } catch {
      // 404 = não encontrado = tudo certo
      setCpfStatus('valid');
      setCpfMsg('CPF válido');
    }
  };

  const verificarCnpj = async (val) => {
    const digitos = val.replace(/\D/g, '');
    if (digitos.length !== 14) {
      setCnpjStatus('invalid');
      setCnpjMsg('CNPJ deve ter 14 dígitos');
      setErrors(p => ({ ...p, cnpj: 'CNPJ deve ter 14 dígitos' }));
      return;
    }
    setCnpjStatus('checking');
    setCnpjMsg('Verificando CNPJ...');
    setErrors(p => { const n = { ...p }; delete n.cnpj; return n; });
    try {
      const token = localStorage.getItem('jwtToken');
      const res = await axios.get(`${API_BASE_URL}/clientes/cnpj/${digitos}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data) {
        setCnpjStatus('duplicate');
        setCnpjMsg('CNPJ já cadastrado');
        setErrors(p => ({ ...p, cnpj: 'CNPJ já cadastrado' }));
      } else {
        setCnpjStatus('valid');
        setCnpjMsg('CNPJ válido');
      }
    } catch {
      // 404 = não encontrado = tudo certo
      setCnpjStatus('valid');
      setCnpjMsg('CNPJ válido');
    }
  };

  const handleCpf = (e) => {
    const v = e.target.value;
    setForm(p => ({ ...p, cpf: v }));
    if (v.replace(/\D/g, '').length === 11) verificarCpf(v);
    else { setCpfStatus(null); setCpfMsg(''); setErrors(p => { const n = { ...p }; delete n.cpf; return n; }); }
  };

  const handleCnpj = (e) => {
    const v = e.target.value;
    setForm(p => ({ ...p, cnpj: v }));
    if (v.replace(/\D/g, '').length === 14) verificarCnpj(v);
    else { setCnpjStatus(null); setCnpjMsg(''); setErrors(p => { const n = { ...p }; delete n.cnpj; return n; }); }
  };

  // Lupa do CNPJ: só habilita com CNPJ completo e ainda não cadastrado
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);
  const podeConsultarCnpj = (form.cnpj || '').replace(/\D/g, '').length === 14
    && cnpjStatus === 'valid'
    && !saving
    && !buscandoCnpj;

  // Substitui nome, contato e endereço pelos do CNPJ consultado (inclusive limpando o que a
  // consulta não trouxe), para não sobrar dado de um CNPJ consultado antes.
  const preencherPorCnpj = async () => {
    if (!podeConsultarCnpj) return;
    setBuscandoCnpj(true);
    const token = localStorage.getItem('jwtToken');
    const { dados, erro } = await consultarCnpj(form.cnpj, API_BASE_URL, { headers: { Authorization: `Bearer ${token}` } });
    setBuscandoCnpj(false);

    if (!dados) {
      addToast('error', erro);
      return;
    }

    setForm(p => {
      const novo = { ...p };
      Object.keys(dados).forEach(campo => {
        novo[campo] = dados[campo] || '';
      });
      return novo;
    });
    const camposOpcionais = {
      email: 'e-mail', telefone: 'telefone', cep: 'CEP', logradouro: 'endereço', bairro: 'bairro', cidade: 'cidade',
    };
    const faltando = Object.keys(camposOpcionais).filter(campo => !dados[campo]).map(campo => camposOpcionais[campo]);
    if (faltando.length > 0) {
      addToast('warning', `Dados preenchidos pelo CNPJ. Não encontrei: ${faltando.join(', ')}. Preencha manualmente.`);
    } else {
      addToast('success', 'Dados preenchidos a partir do CNPJ.');
    }
  };

  const buscarCep = async (cep) => {
    const c = cep.replace(/\D/g, '');
    if (c.length !== 8) return;
    setLoadingCep(true);
    try {
      const res = await axios.get(`https://viacep.com.br/ws/${c}/json/`);
      if (res.data && !res.data.erro) {
        setForm(p => ({
          ...p,
          logradouro: res.data.logradouro || '',
          bairro:     res.data.bairro     || '',
          cidade:     res.data.localidade || '',
          uf:         res.data.uf         || '',
        }));
        addToast('success', 'Endereço preenchido pelo CEP!');
      } else {
        addToast('warning', 'CEP não encontrado');
      }
    } catch { addToast('error', 'Erro ao buscar CEP'); }
    finally { setLoadingCep(false); }
  };

  const validar = () => {
    const e = {};
    if (!form.nome.trim() || form.nome.trim().length < 3) e.nome = 'Nome deve ter pelo menos 3 caracteres';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'E-mail inválido';
    const cpfDigitos = form.cpf ? form.cpf.replace(/\D/g, '') : '';
    const cnpjDigitos = form.cnpj ? form.cnpj.replace(/\D/g, '') : '';
    if (cpfDigitos) {
      if (cpfDigitos.length !== 11) e.cpf = 'CPF deve ter 11 dígitos';
      else if (cpfStatus === 'duplicate') e.cpf = 'CPF já cadastrado';
    }
    if (cnpjDigitos) {
      if (cnpjDigitos.length !== 14) e.cnpj = 'CNPJ deve ter 14 dígitos';
      else if (cnpjStatus === 'duplicate') e.cnpj = 'CNPJ já cadastrado';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSalvar = async () => {
    if (!validar()) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('jwtToken');
      const payload = {
        nome:        form.nome,
        identidade:  form.identidade  || null,
        cpf:         form.cpf         ? form.cpf.replace(/\D/g, '')         : null,
        cnpj:        form.cnpj        ? form.cnpj.replace(/\D/g, '')        : null,
        telefone:    form.telefone    ? form.telefone.replace(/\D/g, '')    : null,
        email:       form.email       || null,
        logradouro:  form.logradouro  || null,
        numero:      form.numero      || null,
        bairro:      form.bairro      || null,
        complemento: form.complemento || null,
        cep:         form.cep         ? form.cep.replace(/\D/g, '')         : null,
        cidade:      form.cidade      || null,
        uf:          form.uf          || null,
      };

      const response = await axios.post(`${API_BASE_URL}/clientes`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      addToast('success', 'Cliente cadastrado com sucesso!');
      onSalvo(response.data || { ...payload, idCliente: response.data?.idCliente });
    } catch (err) {
      addToast('error', 'Erro ao cadastrar cliente: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const statusBorder = (status, hasErr) => {
    if (hasErr || status === 'invalid' || status === 'duplicate') return '2px solid #e74c3c';
    if (status === 'checking') return '2px solid #f39c12';
    if (status === 'valid')    return '2px solid #27ae60';
    return '2px solid #dde8e8';
  };

  return (
    <NovoCadastroWrapper>
      <NovoCadastroHeader>
        <FaPlus size={15} />
        <span>Cadastrar Novo Cliente</span>
        <NovoCadastroCloseBtn onClick={onCancelar} title="Cancelar cadastro">
          <FaTimes size={13} />
        </NovoCadastroCloseBtn>
      </NovoCadastroHeader>

      <NovoCadastroBody>
        <ImportarPrintCliente valoresAtuais={form} onDadosLidos={aplicarPrint} disabled={saving} />
        {/* Nome */}
        <NCRow>
          <NCGroup full>
            <NCLabel>Nome completo *</NCLabel>
            <NCInput
              value={form.nome} style={destaquePrint('nome')}
              onChange={set('nome')}
              placeholder="Nome completo ou razão social"
              autoFocus
            />
            {errors.nome && <NCError>{errors.nome}</NCError>}
          </NCGroup>
        </NCRow>

        {/* RG + Email */}
        <NCRow>
          <NCGroup>
            <NCLabel><FaIdCard size={11} /> RG / Identidade</NCLabel>
            <NCInput
              value={form.identidade}
              onChange={(e) => setForm(p => ({ ...p, identidade: e.target.value.toUpperCase() }))}
              placeholder="Nº do documento"
              maxLength={20}
            />
            <NCHint>Usado nos requerimentos DETRAN</NCHint>
          </NCGroup>
          <NCGroup>
            <NCLabel><FaEnvelope size={11} /> E-mail</NCLabel>
            <NCInput
              type="email"
              value={form.email} style={destaquePrint('email')}
              onChange={set('email')}
              placeholder="email@exemplo.com"
            />
            {errors.email && <NCError>{errors.email}</NCError>}
          </NCGroup>
        </NCRow>

        {/* CPF + CNPJ */}
        <NCRow>
          <NCGroup>
            <NCLabel><FaIdCard size={11} /> CPF</NCLabel>
            <NCInputRelative>
              <InputMask
                mask="999.999.999-99"
                value={form.cpf}
                onChange={handleCpf}
                placeholder="000.000.000-00"
                style={{ ...destaquePrint('cpf'), border: statusBorder(cpfStatus, errors.cpf) }}
              >
                {(props) => <NCInput {...props} />}
              </InputMask>
              {cpfStatus === 'checking'  && <NCStatusIcon><FaSpinner className="nc-spin" style={{ color: '#f39c12' }} /></NCStatusIcon>}
              {cpfStatus === 'valid'     && <NCStatusIcon><FaCheckCircle style={{ color: '#27ae60' }} /></NCStatusIcon>}
              {(cpfStatus === 'invalid' || cpfStatus === 'duplicate') && <NCStatusIcon><FaTimesCircle style={{ color: '#e74c3c' }} /></NCStatusIcon>}
            </NCInputRelative>
            {cpfMsg && <NCHint style={{ color: cpfStatus === 'valid' ? '#27ae60' : cpfStatus === 'checking' ? '#f39c12' : '#e74c3c' }}>{cpfMsg}</NCHint>}
            {errors.cpf && <NCError>{errors.cpf}</NCError>}
          </NCGroup>
          <NCGroup>
            <NCLabel><FaBuilding size={11} /> CNPJ</NCLabel>
            <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
              <NCInputRelative style={{ flex: 1, minWidth: 0 }}>
                <InputMask
                  mask="99.999.999/9999-99"
                  value={form.cnpj}
                  onChange={handleCnpj}
                  placeholder="00.000.000/0000-00"
                  style={{ ...destaquePrint('cnpj'), border: statusBorder(cnpjStatus, errors.cnpj) }}
                >
                  {(props) => <NCInput {...props} />}
                </InputMask>
                {cnpjStatus === 'checking'  && <NCStatusIcon><FaSpinner className="nc-spin" style={{ color: '#f39c12' }} /></NCStatusIcon>}
                {cnpjStatus === 'valid'     && <NCStatusIcon><FaCheckCircle style={{ color: '#27ae60' }} /></NCStatusIcon>}
                {(cnpjStatus === 'invalid' || cnpjStatus === 'duplicate') && <NCStatusIcon><FaTimesCircle style={{ color: '#e74c3c' }} /></NCStatusIcon>}
              </NCInputRelative>
              <button
                type="button"
                onClick={preencherPorCnpj}
                disabled={!podeConsultarCnpj}
                title={podeConsultarCnpj ? 'Buscar dados do CNPJ (Receita Federal)' : 'Digite um CNPJ válido para buscar os dados'}
                style={{
                  backgroundColor: podeConsultarCnpj ? '#3498db' : '#b8c2cc', color: '#fff', border: 'none',
                  padding: '0 12px', borderRadius: 6, fontSize: 13, display: 'flex', alignItems: 'center',
                  cursor: podeConsultarCnpj ? 'pointer' : 'not-allowed',
                }}
              >
                {buscandoCnpj ? <FaSpinner className="nc-spin" /> : <FaSearch />}
              </button>
            </div>
            {buscandoCnpj && <NCHint>Consultando CNPJ...</NCHint>}
            {cnpjMsg &&<NCHint style={{ color: cnpjStatus === 'valid' ? '#27ae60' : cnpjStatus === 'checking' ? '#f39c12' : '#e74c3c' }}>{cnpjMsg}</NCHint>}
            {errors.cnpj && <NCError>{errors.cnpj}</NCError>}
          </NCGroup>
        </NCRow>

        {/* Telefone */}
        <NCRow>
          <NCGroup full>
            <NCLabel><FaPhone size={11} /> Telefone</NCLabel>
            <InputMask
              mask="(99) 99999-9999"
              maskChar={null}
              value={form.telefone} style={destaquePrint('telefone')}
              onChange={set('telefone')}
              placeholder="(00) 00000-0000"
            >
              {(props) => <NCInput {...props} />}
            </InputMask>
          </NCGroup>
        </NCRow>

        <NCSectionLabel><FaMapMarkerAlt size={11} /> Endereço</NCSectionLabel>

        {/* CEP + Cidade + UF */}
        <NCRow>
          <NCGroup style={{ flex: '0 0 130px' }}>
            <NCLabel><FaMapPin size={11} /> CEP</NCLabel>
            <InputMask
              mask="99999-999"
              value={form.cep} style={destaquePrint('cep')}
              onChange={set('cep')}
              onBlur={(e) => { if (cepVeioDoPrint(e.target.value, cepDoPrint)) return; buscarCep(e.target.value); }}
              placeholder="00000-000"
            >
              {(props) => <NCInput {...props} />}
            </InputMask>
            {loadingCep && <NCHint>Buscando endereço...</NCHint>}
          </NCGroup>
          <NCGroup style={{ flex: 2 }}>
            <NCLabel><FaCity size={11} /> Cidade</NCLabel>
            <NCInput value={form.cidade} style={destaquePrint('cidade')} onChange={set('cidade')} placeholder="Cidade" />
          </NCGroup>
          <NCGroup style={{ flex: '0 0 65px' }}>
            <NCLabel><FaGlobeAmericas size={11} /> UF</NCLabel>
            <NCInput value={form.uf} style={destaquePrint('uf')} onChange={(e) => setForm(p => ({ ...p, uf: e.target.value.toUpperCase() }))} placeholder="UF" maxLength={2} />
          </NCGroup>
        </NCRow>

        {/* Logradouro + Número */}
        <NCRow>
          <NCGroup style={{ flex: 3 }}>
            <NCLabel><FaRoad size={11} /> Logradouro</NCLabel>
            <NCInput value={form.logradouro} style={destaquePrint('logradouro')} onChange={set('logradouro')} placeholder="Rua, Avenida..." />
          </NCGroup>
          <NCGroup style={{ flex: '0 0 100px' }}>
            <NCLabel><FaHome size={11} /> Número</NCLabel>
            <NCInput value={form.numero} style={destaquePrint('numero')} onChange={set('numero')} placeholder="Nº" />
          </NCGroup>
        </NCRow>

        {/* Bairro + Complemento */}
        <NCRow>
          <NCGroup>
            <NCLabel>Bairro</NCLabel>
            <NCInput value={form.bairro} style={destaquePrint('bairro')} onChange={set('bairro')} placeholder="Bairro" />
          </NCGroup>
          <NCGroup>
            <NCLabel>Complemento</NCLabel>
            <NCInput value={form.complemento} style={destaquePrint('complemento')} onChange={set('complemento')} placeholder="Apto, bloco..." />
          </NCGroup>
        </NCRow>
      </NovoCadastroBody>

      <NovoCadastroFooter>
        <EditCancelBtn onClick={onCancelar}>Cancelar</EditCancelBtn>
        <EditSaveBtn onClick={handleSalvar} disabled={saving}>
          <FaSave size={13} /> {saving ? 'Cadastrando...' : 'Cadastrar e Selecionar'}
        </EditSaveBtn>
      </NovoCadastroFooter>

      <style>{`
        @keyframes nc-spin {
          from { transform: translateY(-50%) rotate(0deg); }
          to   { transform: translateY(-50%) rotate(360deg); }
        }
        .nc-spin { animation: nc-spin 1s linear infinite; position: absolute; }
      `}</style>
    </NovoCadastroWrapper>
  );
};

// ─── Modal do Adquirente (Requerimento de Preenchimento da ATPV-e) ───────────
// Abre ao continuar com a ATPV-e: busca o adquirente no cadastro (nome ou CPF/CNPJ) ou cadastra um novo
// (mesmo formulário de novo cliente, com a lupa do CNPJ). O escolhido vai preenchido para a folha.

const SelecionarAdquirenteModal = ({ vendedor, onEscolher, onPular, onClose, API_BASE_URL }) => {
  const { addToast } = useToast();
  const [termo, setTermo]             = useState('');
  const [resultados, setResultados]   = useState([]);
  const [buscando, setBuscando]       = useState(false);
  const [buscou, setBuscou]           = useState(false);
  const [mostrarNovo, setMostrarNovo] = useState(false);
  // Valor da venda: obrigatório — sai impresso no requerimento e, como o resto vem preenchido
  // automaticamente, é o único dado que não pode ser esquecido.
  const [valor, setValor]             = useState(null);
  const [erroValor, setErroValor]     = useState('');
  const valorOk = valor != null && valor > 0;

  const exigirValor = () => {
    if (valorOk) return true;
    setErroValor('Informe o valor da venda para continuar.');
    addToast('warning', 'Informe o valor da venda: ele sai impresso no requerimento da ATPV-e.');
    return false;
  };

  const documentoDe = (c) => {
    const cpf = (c.cpf || '').replace(/\D/g, '');
    const cnpj = (c.cnpj || '').replace(/\D/g, '');
    if (cpf.length === 11) return `CPF: ${cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}`;
    if (cnpj.length === 14) return `CNPJ: ${cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')}`;
    return null;
  };

  // Busca por CPF/CNPJ (11 ou 14 dígitos) ou, senão, por nome
  const buscar = async () => {
    const t = termo.trim();
    const digitos = t.replace(/\D/g, '');
    const porDocumento = /^[\d.\-/\s]+$/.test(t) && (digitos.length === 11 || digitos.length === 14);
    if (!porDocumento && t.length < 3) {
      addToast('warning', 'Digite ao menos 3 letras do nome ou o CPF/CNPJ completo.');
      return;
    }
    setBuscando(true);
    setMostrarNovo(false);
    try {
      const auth = { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } };
      let lista = [];
      if (porDocumento) {
        const tipo = digitos.length === 11 ? 'cpf' : 'cnpj';
        const res = await axios.get(`${API_BASE_URL}/clientes/${tipo}/${digitos}`, auth);
        lista = res.data ? [res.data] : [];
      } else {
        const res = await axios.get(`${API_BASE_URL}/clientes/nome/${encodeURIComponent(t.toLowerCase())}`, auth);
        lista = Array.isArray(res.data) ? res.data : [];
      }
      setResultados(lista);
    } catch (error) {
      const status = error.response?.status;
      if (status !== 404 && status !== 204) addToast('error', 'Erro ao buscar adquirente: ' + (error.response?.data?.message || error.message));
      setResultados([]);
    } finally {
      setBuscando(false);
      setBuscou(true);
    }
  };

  const escolher = (cliente) => {
    if (!documentoDe(cliente)) {
      addToast('warning', 'Este cliente não tem CPF/CNPJ cadastrado. Complete o cadastro dele antes de usá-lo como adquirente.');
      return;
    }
    if (vendedor && (cliente.idCliente || cliente.id) === (vendedor.idCliente || vendedor.id)) {
      addToast('warning', 'O adquirente não pode ser o próprio vendedor.');
      return;
    }
    if (!exigirValor()) return;
    onEscolher(cliente, valor);
  };

  const pular = () => {
    if (!exigirValor()) return;
    onPular(valor);
  };

  return (
    <EditOverlay onClick={onClose}>
      <EditContainer onClick={(e) => e.stopPropagation()}>
        <EditHeader>
          <FaUserTie size={16} />
          <span>Adquirente (comprador) — Preenchimento da ATPV-e</span>
          <EditCloseBtn onClick={onClose} title="Voltar"><FaTimes /></EditCloseBtn>
        </EditHeader>
        <EditBody>
          <HelperText style={{ display: 'block', marginBottom: 10, fontSize: 12 }}>
            Busque o adquirente pelo nome ou CPF/CNPJ. Os dados dele serão preenchidos automaticamente no requerimento.
          </HelperText>
          <SearchContainer>
            <SearchInput
              value={termo}
              onChange={(e) => setTermo(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && buscar()}
              placeholder="Nome ou CPF/CNPJ do adquirente"
              autoFocus
            />
            <SearchButton onClick={buscar} disabled={buscando}>
              {buscando ? <FaSpinner className="nc-spin" /> : <FaSearch />} Buscar
            </SearchButton>
          </SearchContainer>

          <EditRow style={{ marginTop: 14, marginBottom: 4 }}>
            <EditGroup>
              <EditLabel>Valor da venda * (sai impresso no requerimento)</EditLabel>
              <NumericFormat
                value={valor ?? ''}
                onValueChange={(v, info) => {
                  if (info.source !== 'event') return;
                  setValor(v.floatValue ?? null);
                  setErroValor('');
                }}
                thousandSeparator="." decimalSeparator="," decimalScale={2} fixedDecimalScale
                prefix="R$ " placeholder="R$ 0,00"
                customInput={EditInput}
                style={erroValor ? { borderColor: '#e74c3c' } : undefined}
              />
              {erroValor
                ? <NCError>{erroValor}</NCError>
                : <EditHint>Obrigatório: sem o valor, o requerimento da ATPV-e não é liberado para impressão.</EditHint>}
            </EditGroup>
          </EditRow>

          {resultados.length > 0 && (
            <ClientesListContainer style={{ marginTop: 12 }}>
              {resultados.map((c, i) => {
                const doc = documentoDe(c);
                return (
                  <ClienteListItem key={c.idCliente || i}>
                    <ClienteListAvatar><FaUser size={16} /></ClienteListAvatar>
                    <ClienteListInfo>
                      <ClienteListNome>{c.nome}</ClienteListNome>
                      <ClienteListDetalhes>
                        {doc ? <ClienteListDoc><FaIdCard size={11} />{doc}</ClienteListDoc>
                          : <ClienteListDocAlerta><FaExclamationTriangle size={9} /> CPF/CNPJ obrigatório</ClienteListDocAlerta>}
                        {c.cidade && <span>{c.cidade}{c.uf ? `/${c.uf}` : ''}</span>}
                      </ClienteListDetalhes>
                    </ClienteListInfo>
                    <ClienteListAcoes>
                      <ClienteListBtnSelecionar onClick={() => escolher(c)} disabled={!doc}>
                        <FaCheck size={11} /> Selecionar
                      </ClienteListBtnSelecionar>
                    </ClienteListAcoes>
                  </ClienteListItem>
                );
              })}
            </ClientesListContainer>
          )}

          {buscou && !buscando && resultados.length === 0 && !mostrarNovo && (
            <NaoEncontradoBanner style={{ marginTop: 12 }}>
              <NaoEncontradoTexto>
                <NaoEncontradoIcone><FaExclamationTriangle /></NaoEncontradoIcone>
                <div>
                  <NaoEncontradoTitulo>Adquirente não encontrado</NaoEncontradoTitulo>
                  <NaoEncontradoSubtitulo>Cadastre o adquirente para preencher o requerimento.</NaoEncontradoSubtitulo>
                </div>
              </NaoEncontradoTexto>
              <CadastrarNovoBtn onClick={() => setMostrarNovo(true)}><FaPlus /> Cadastrar adquirente</CadastrarNovoBtn>
            </NaoEncontradoBanner>
          )}

          {!mostrarNovo && (buscou && resultados.length > 0) && (
            <div style={{ marginTop: 10, textAlign: 'right' }}>
              <CadastrarNovoBtn onClick={() => setMostrarNovo(true)}><FaPlus /> Cadastrar outro adquirente</CadastrarNovoBtn>
            </div>
          )}

          {mostrarNovo && (
            <div style={{ marginTop: 12 }}>
              <NovoClienteInline
                onSalvo={(novo) => { setMostrarNovo(false); escolher(novo); }}
                onCancelar={() => setMostrarNovo(false)}
                API_BASE_URL={API_BASE_URL}
              />
            </div>
          )}
        </EditBody>
        <EditFooter>
          <EditCancelBtn onClick={onClose}>Voltar</EditCancelBtn>
          <EditCancelBtn onClick={pular} title="Abre o requerimento com os campos do adquirente em branco">
            Continuar sem adquirente
          </EditCancelBtn>
        </EditFooter>
      </EditContainer>
    </EditOverlay>
  );
};

// ─── Modal Principal ──────────────────────────────────────────────────────────

const RequerimentoDetranModal = ({ isOpen, onClose, userData }) => {
  const navigate = useNavigate();
  const { API_BASE_URL } = config;
  const { addToast } = useToast();

  const [buscaCliente, setBuscaCliente]           = useState('');
  const [tipoBusca, setTipoBusca]                 = useState('nome');
  const [clienteEncontrado, setClienteEncontrado] = useState(null);
  const [loading, setLoading]                     = useState(false);
  const [clientesList, setClientesList]           = useState([]);
  const [mostrarLista, setMostrarLista]           = useState(false);
  const [tipoRequerimento, setTipoRequerimento]   = useState('SERVICO_DECLARACAO');
  const [editModalOpen, setEditModalOpen]         = useState(false);
  // ── NOVO: cliente sendo editado no momento — desacoplado do cliente já
  // selecionado, pois agora também é possível editar direto na lista de
  // resultados (antes de selecionar), pra completar o CPF/CNPJ faltante. ──
  const [clienteEmEdicao, setClienteEmEdicao]     = useState(null);
  const [mostrarNovoCadastro, setMostrarNovoCadastro] = useState(false);
  const [buscaRealizada, setBuscaRealizada]       = useState(false);

  const [placa, setPlaca]   = useState('');
  const [chassi, setChassi] = useState('');
  const [modalAdquirenteAberto, setModalAdquirenteAberto] = useState(false);
  // Dados do cadastro de veículos que não têm campo neste modal, mas vão para o requerimento (ex.: marca/modelo)
  const [dadosVeiculo, setDadosVeiculo] = useState({ renavam: '', motor: '', marcaModelo: '', anoFabricacaoModelo: '' });
  const [veiculoMsg, setVeiculoMsg]     = useState('');
  const buscaVeiculoRef = useRef(0);

  const tiposRequerimento = [
    { id: 'SERVICO_DECLARACAO',    nome: 'REQUERIMENTO DE SERVIÇO COM DECLARAÇÃO DE AUTENTICIDADE DE DOCUMENTOS' },
    { id: 'PRIMEIRO_EMPLACAMENTO', nome: 'REQUERIMENTO DE SERVIÇO PARA O PROCESSO DE PRIMEIRO EMPLACAMENTO' },
    { id: 'SEGUNDA_VIA_CRV',       nome: 'REQUERIMENTO PARA SOLICITAÇÃO DE 2ª VIA DE CRV/ATPV-e OU VALIDAÇÃO DE CRV' },
    { id: 'BAIXA_DEFINITIVA',      nome: 'REQUERIMENTO PARA BAIXA DEFINITIVA (CONFORME RESOLUÇÃO CONTRAN Nº 967/22)' },
    { id: 'PREENCHIMENTO_ATPVE',   nome: 'REQUERIMENTO DE PREENCHIMENTO DA ATPV-e (ANEXO 2 DO POP 02)' },
    { id: 'DECLARACAO_RESIDENCIA', nome: 'DECLARAÇÃO DE RESIDÊNCIA' },
  ];

  const getTipoRequerimentoNome = (id) =>
    tiposRequerimento.find(t => t.id === id)?.nome || id;

  const resetBusca = () => {
    setClienteEncontrado(null);
    setClientesList([]);
    setMostrarLista(false);
    setMostrarNovoCadastro(false);
    setBuscaRealizada(false);
  };

  const buscarCliente = async () => {
    if (!buscaCliente.trim()) {
      addToast('warning', `Digite o ${tipoBusca === 'nome' ? 'nome' : 'CPF/CNPJ'} do cliente`);
      return;
    }

    setLoading(true);
    setMostrarNovoCadastro(false);
    setBuscaRealizada(false);

    try {
      const token = localStorage.getItem('jwtToken');

      if (tipoBusca === 'cpf') {
        const docLimpo = buscaCliente.replace(/\D/g, '');
        const endpoint = docLimpo.length <= 11
          ? `${API_BASE_URL}/clientes/cpf/${docLimpo}`
          : `${API_BASE_URL}/clientes/cnpj/${docLimpo}`;

        const response = await axios.get(endpoint, { headers: { Authorization: `Bearer ${token}` } });

        if (response.data) {
          setClienteEncontrado(response.data);
          setClientesList([]);
          setMostrarLista(false);
          setBuscaRealizada(true);
          addToast('success', 'Cliente encontrado com sucesso!');
        } else {
          setBuscaRealizada(true);
          setClienteEncontrado(null);
          setClientesList([]);
          setMostrarLista(false);
          addToast('warning', 'Cliente não encontrado');
        }
      } else {
        const nomeBusca = buscaCliente.trim().toLowerCase();
        const response = await axios.get(
          `${API_BASE_URL}/clientes/nome/${encodeURIComponent(nomeBusca)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data && response.data.length > 0) {
          if (response.data.length === 1) {
            setClienteEncontrado(response.data[0]);
            setClientesList([]);
            setMostrarLista(false);
            addToast('success', 'Cliente encontrado com sucesso!');
          } else {
            setClientesList(response.data);
            setMostrarLista(true);
            setClienteEncontrado(null);
            addToast('info', `${response.data.length} clientes encontrados. Selecione um da lista.`);
          }
          setBuscaRealizada(true);
        } else {
          setBuscaRealizada(true);
          setClienteEncontrado(null);
          setClientesList([]);
          setMostrarLista(false);
          addToast('warning', 'Nenhum cliente encontrado');
        }
      }
    } catch (error) {
      const status = error.response?.status;
      if (status === 404 || status === 204) {
        // Não encontrado — exibe opção de cadastrar
        setBuscaRealizada(true);
        setClienteEncontrado(null);
        setClientesList([]);
        setMostrarLista(false);
      } else {
        addToast('error', 'Erro ao buscar cliente: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const selecionarCliente = (cliente) => {
    setClienteEncontrado(cliente);
    setClientesList([]);
    setMostrarLista(false);
    setBuscaRealizada(true);
    addToast('success', `Cliente ${cliente.nome} selecionado!`);
  };

  const handleClienteCadastrado = (novoCliente) => {
    setClienteEncontrado(novoCliente);
    setMostrarNovoCadastro(false);
    setBuscaRealizada(true);
  };

  const formatarCPF  = (v) => v ? v.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : '';
  const formatarCNPJ = (v) => v ? v.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5') : '';
  const formatarTelefone = (v) => {
    if (!v) return '';
    const d = v.replace(/\D/g, '');
    if (d.length === 11) return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
    return v;
  };

  // ── CORREÇÃO: antes bastava o campo cpf/cnpj não estar vazio pra formatar
  // e considerar "tem documento" — aceitando valores mal formados (ex: CPF
  // com um dígito a mais) como válidos. Isso deixava esse modal e o
  // OrdemServico.jsx/PainelCRVA.jsx discordarem sobre o mesmo cliente. Agora
  // a regra é a mesma em todo o sistema: exige exatamente 11 dígitos pro CPF
  // ou 14 pro CNPJ. ──
  const formatarDocumentoLista = (cliente) => {
    const cpfDigitos = (cliente.cpf || '').replace(/\D/g, '');
    const cnpjDigitos = (cliente.cnpj || '').replace(/\D/g, '');
    if (cpfDigitos.length === 11)  return `CPF: ${formatarCPF(cpfDigitos)}`;
    if (cnpjDigitos.length === 14) return `CNPJ: ${formatarCNPJ(cnpjDigitos)}`;
    return null;
  };

  const formatarPlacaModal = (valor) => {
    const v = (valor || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7);
    return v.length > 3 ? v.slice(0, 3) + '-' + v.slice(3) : v;
  };

  // Procura no cadastro de veículos e completa os campos vazios. Só a última busca vale (digitação rápida).
  const puxarVeiculoCadastrado = async (filtro, preencher) => {
    const busca = ++buscaVeiculoRef.current;
    const veiculo = await buscarVeiculo(API_BASE_URL, filtro);
    if (busca !== buscaVeiculoRef.current || !veiculo) return;
    preencher(veiculo);
    setDadosVeiculo({
      renavam: veiculo.renavam || '',
      motor: veiculo.motor || '',
      marcaModelo: veiculo.marcaModelo || '',
      anoFabricacaoModelo: veiculo.anoFabricacaoModelo || '',
    });
    setVeiculoMsg('Veículo encontrado no cadastro: dados preenchidos. Confira antes de continuar.');
  };

  const handlePlacaChange = (e) => {
    const v = formatarPlacaModal(e.target.value);
    setPlaca(v);
    // placa trocada: os dados de outro veículo não valem mais
    buscaVeiculoRef.current++;
    setDadosVeiculo({ renavam: '', motor: '', marcaModelo: '', anoFabricacaoModelo: '' });
    setVeiculoMsg('');
    const limpa = v.replace(/[^A-Z0-9]/g, '');
    if (/^[A-Z]{3}\d[A-Z0-9]\d{2}$/.test(limpa)) {
      puxarVeiculoCadastrado({ placa: limpa }, (veiculo) => {
        if (veiculo.chassi) setChassi(veiculo.chassi);
      });
    }
  };

  const handleChassiChange = (e) => {
    const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setChassi(v);
    // Sem placa (ex.: primeiro emplacamento), procura pelo chassi completo
    if (!placa && v.length === 17) {
      puxarVeiculoCadastrado({ chassi: v }, (veiculo) => {
        if (veiculo.placa) setPlaca(formatarPlacaModal(veiculo.placa));
      });
    }
  };

  // Documento importado: preenche placa e chassi (os campos deste modal) e guarda o resto para o requerimento.
  // A gravação no cadastro de veículos é feita pelo próprio botão de importar.
  const handleDocumentoVeiculoLido = (dados) => {
    buscaVeiculoRef.current++;
    if (dados.placa) setPlaca(formatarPlacaModal(dados.placa));
    if (dados.chassi) setChassi(dados.chassi);
    setDadosVeiculo({
      renavam: dados.renavam || '',
      motor: dados.motor || '',
      marcaModelo: dados.marcaModelo || '',
      anoFabricacaoModelo: dados.anoFabricacaoModelo || '',
    });
    setVeiculoMsg('');
  };

  const handleCpfCnpjChange = (e) => {
    let v = e.target.value.replace(/\D/g, '');
    if (v.length <= 11) {
      if (v.length <= 3)       v = v.replace(/(\d{0,3})/, '$1');
      else if (v.length <= 6)  v = v.replace(/(\d{3})(\d{0,3})/, '$1.$2');
      else if (v.length <= 9)  v = v.replace(/(\d{3})(\d{3})(\d{0,3})/, '$1.$2.$3');
      else                     v = v.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, '$1.$2.$3-$4');
    } else {
      v = v.slice(0, 14);
      if (v.length <= 5)       v = v.replace(/(\d{2})(\d{0,3})/, '$1.$2');
      else if (v.length <= 8)  v = v.replace(/(\d{2})(\d{3})(\d{0,3})/, '$1.$2.$3');
      else if (v.length <= 12) v = v.replace(/(\d{2})(\d{3})(\d{3})(\d{0,4})/, '$1.$2.$3/$4');
      else                     v = v.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, '$1.$2.$3/$4-$5');
    }
    setBuscaCliente(v);
  };

  const requerVeiculo = ['SERVICO_DECLARACAO', 'PRIMEIRO_EMPLACAMENTO', 'SEGUNDA_VIA_CRV', 'BAIXA_DEFINITIVA', 'PREENCHIMENTO_ATPVE'].includes(tipoRequerimento);

  // adquirente: só na ATPV-e. undefined = ainda não escolhido (abre o modal do adquirente);
  // null = continuar sem adquirente; objeto = cliente adquirente escolhido.
  const handleAbrirRequerimento = async (adquirente, valorVenda = null) => {
    if (!clienteEncontrado) {
      addToast('warning', 'Selecione um cliente primeiro');
      return;
    }
    if (tipoRequerimento === 'PREENCHIMENTO_ATPVE' && adquirente === undefined) {
      setModalAdquirenteAberto(true);
      return;
    }
    setModalAdquirenteAberto(false);
    // Guarda o veículo digitado/importado no cadastro de veículos, para puxar pela placa na próxima vez.
    // Não bloqueia: se falhar, o requerimento abre do mesmo jeito.
    if (requerVeiculo && (placa || chassi)) {
      await salvarVeiculo(API_BASE_URL, { placa, chassi, ...dadosVeiculo });
    }
    const payload = {
      cliente:              clienteEncontrado,
      tipoRequerimento,
      tipoRequerimentoNome: getTipoRequerimentoNome(tipoRequerimento),
      empresaId:            userData?.empresaId,
      usuarioNome:          userData?.nomeUsuario,
      cidadeEmpresa:        userData?.empresaCidade || '',
      placa,
      chassi,
      marcaModelo:          requerVeiculo ? dadosVeiculo.marcaModelo : '',
      adquirente:           tipoRequerimento === 'PREENCHIMENTO_ATPVE' ? (adquirente || null) : null,
      valorVenda:           tipoRequerimento === 'PREENCHIMENTO_ATPVE' ? valorVenda : null,
    };
    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
    window.open(`/requerimento-detran?data=${encoded}`, '_blank');
    onClose();
  };

  // ── NOVO: abre o modal de edição para QUALQUER cliente — seja o já
  // selecionado (card abaixo da busca) ou um item ainda na lista de
  // resultados múltiplos (antes de selecionar). ──
  const abrirEdicaoCliente = (cliente) => {
    setClienteEmEdicao(cliente);
    setEditModalOpen(true);
  };

  const handleClienteEditado = (clienteAtualizado) => {
    const idAtualizado = clienteAtualizado.idCliente || clienteAtualizado.id;

    // ── CORREÇÃO: atualiza o cliente também dentro da lista de resultados
    // (busca por nome), sem precisar buscar de novo — assim que o CPF/CNPJ
    // é preenchido na edição, o botão "Selecionar" daquele item da lista
    // libera sozinho, pois "tem documento" é recalculado a cada render. ──
    setClientesList(prev => prev.map(c =>
      (c.idCliente || c.id) === idAtualizado ? { ...c, ...clienteAtualizado } : c
    ));

    // Se o cliente editado for o mesmo já selecionado, atualiza o card também
    if (clienteEncontrado && (clienteEncontrado.idCliente || clienteEncontrado.id) === idAtualizado) {
      setClienteEncontrado(clienteAtualizado);
    }

    setEditModalOpen(false);
    setClienteEmEdicao(null);
  };

  // Exibir banner "não encontrado + botão cadastrar" quando busca não trouxe resultado
  const exibirBannerNaoEncontrado =
    buscaRealizada &&
    !clienteEncontrado &&
    !mostrarLista &&
    !mostrarNovoCadastro;

  if (!isOpen) return null;

  return (
    <>
      <ModalOverlay onClick={onClose}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <MdRequestPage size={24} />
            <ModalTitle>Requerimento DETRAN/RS</ModalTitle>
            <CloseButton onClick={onClose}>&times;</CloseButton>
          </ModalHeader>

          <ModalContent>
            {/* Busca */}
            <Section>
              <SectionTitle><FaUserTie />Buscar Cliente</SectionTitle>

              <TipoBuscaContainer>
                <TipoBuscaButton
                  active={tipoBusca === 'nome'}
                  onClick={() => { setTipoBusca('nome'); setBuscaCliente(''); resetBusca(); }}
                >
                  <FaUser size={14} /> Buscar por Nome
                </TipoBuscaButton>
                <TipoBuscaButton
                  active={tipoBusca === 'cpf'}
                  onClick={() => { setTipoBusca('cpf'); setBuscaCliente(''); resetBusca(); }}
                >
                  <FaIdCard size={14} /> Buscar por CPF/CNPJ
                </TipoBuscaButton>
              </TipoBuscaContainer>

              <SearchContainer>
                <SearchInput
                  type="text"
                  value={buscaCliente}
                  onChange={tipoBusca === 'cpf' ? handleCpfCnpjChange : (e) => setBuscaCliente(e.target.value)}
                  placeholder={tipoBusca === 'nome' ? 'Digite o nome do cliente...' : 'Digite o CPF ou CNPJ...'}
                  onKeyPress={(e) => e.key === 'Enter' && buscarCliente()}
                  maxLength={tipoBusca === 'cpf' ? 18 : undefined}
                />
                <SearchButton onClick={buscarCliente} disabled={loading}>
                  <FaSearch /> {loading ? 'Buscando...' : 'Buscar'}
                </SearchButton>
              </SearchContainer>

              {/* Lista múltiplos clientes */}
              {mostrarLista && clientesList.length > 0 && tipoBusca === 'nome' && (
                <ClientesListContainer>
                  <ListTitle>Selecione o cliente desejado:</ListTitle>
                  {clientesList.map((cliente, index) => {
                    const docFormatado = formatarDocumentoLista(cliente);
                    // ── CORREÇÃO: sem CPF/CNPJ, o cliente fica "inativo" pra
                    // seleção — só o botão de editar continua liberado. A
                    // NFS-e nacional exige o documento pra gerar a DPS, então
                    // evitamos selecionar um cliente que ainda não pode ser
                    // usado num requerimento. Assim que o CPF/CNPJ é salvo na
                    // edição, o botão libera sozinho (recalculado a cada
                    // render). ──
                    const semDocumento = !docFormatado;
                    return (
                      <ClienteListItem key={index}>
                        <ClienteListAvatar><FaUser size={16} /></ClienteListAvatar>
                        <ClienteListInfo>
                          <ClienteListNome>{cliente.nome}</ClienteListNome>
                          <ClienteListDetalhes>
                            {cliente.telefone && <span>Tel: {formatarTelefone(cliente.telefone)}</span>}
                            {docFormatado && (
                              <ClienteListDoc>
                                <FaIdCard size={11} />
                                {docFormatado}
                              </ClienteListDoc>
                            )}
                            {semDocumento && (
                              <ClienteListDocAlerta>
                                <FaExclamationTriangle size={9} /> CPF/CNPJ obrigatório
                              </ClienteListDocAlerta>
                            )}
                          </ClienteListDetalhes>
                        </ClienteListInfo>
                        <ClienteListAcoes>
                          <ClienteListBtnEditar
                            onClick={() => abrirEdicaoCliente(cliente)}
                            title="Editar dados do cliente"
                          >
                            <FaEdit size={12} />
                          </ClienteListBtnEditar>
                          <ClienteListBtnSelecionar
                            onClick={() => !semDocumento && selecionarCliente(cliente)}
                            disabled={semDocumento}
                            title={semDocumento ? 'Cadastre o CPF ou CNPJ deste cliente antes de selecionar' : undefined}
                          >
                            <FaCheck size={11} /> Selecionar
                          </ClienteListBtnSelecionar>
                        </ClienteListAcoes>
                      </ClienteListItem>
                    );
                  })}
                </ClientesListContainer>
              )}

              {/* Banner: nenhum resultado + botão cadastrar */}
              {exibirBannerNaoEncontrado && (
                <NaoEncontradoBanner>
                  <NaoEncontradoTexto>
                    <NaoEncontradoIcone>
                      <FaUser size={22} />
                    </NaoEncontradoIcone>
                    <div>
                      <NaoEncontradoTitulo>Nenhum cliente encontrado</NaoEncontradoTitulo>
                      <NaoEncontradoSubtitulo>
                        Deseja cadastrar um novo cliente com os dados buscados?
                      </NaoEncontradoSubtitulo>
                    </div>
                  </NaoEncontradoTexto>
                  <CadastrarNovoBtn onClick={() => setMostrarNovoCadastro(true)}>
                    <FaPlus size={13} /> Cadastrar Novo Cliente
                  </CadastrarNovoBtn>
                </NaoEncontradoBanner>
              )}

              {/* Formulário inline de novo cadastro */}
              {mostrarNovoCadastro && (
                <NovoClienteInline
                  onSalvo={handleClienteCadastrado}
                  onCancelar={() => setMostrarNovoCadastro(false)}
                  API_BASE_URL={API_BASE_URL}
                />
              )}

              {/* Cliente selecionado */}
              {clienteEncontrado && !mostrarLista && (
                <ClienteCard>
                  <ClienteHeader>
                    <ClienteAvatar>
                      {clienteEncontrado.cpf ? <FaUser size={20} /> : <FaUserTie size={20} />}
                    </ClienteAvatar>
                    <ClienteInfo>
                      <ClienteNomeRow>
                        <ClienteNome>{clienteEncontrado.nome || clienteEncontrado.razaoSocial}</ClienteNome>
                        <EditarBtn onClick={() => abrirEdicaoCliente(clienteEncontrado)} title="Editar dados do cliente">
                          <FaEdit size={12} /> editar dados
                        </EditarBtn>
                      </ClienteNomeRow>
                      <ClienteDetalhes>
                        {clienteEncontrado.cpf && (
                          <span><FaIdCard size={12} /> CPF: {formatarCPF(clienteEncontrado.cpf)}</span>
                        )}
                        {clienteEncontrado.cnpj && (
                          <span><FaIdCard size={12} /> CNPJ: {formatarCNPJ(clienteEncontrado.cnpj)}</span>
                        )}
                        {clienteEncontrado.telefone && (
                          <span><FaPhone size={12} /> {formatarTelefone(clienteEncontrado.telefone)}</span>
                        )}
                        {clienteEncontrado.email && (
                          <span><FaEnvelope size={12} /> {clienteEncontrado.email}</span>
                        )}
                      </ClienteDetalhes>
                    </ClienteInfo>
                  </ClienteHeader>
                  {(clienteEncontrado.logradouro || clienteEncontrado.cidade) && (
                    <ClienteEndereco>
                      {clienteEncontrado.logradouro && `${clienteEncontrado.logradouro}, `}
                      {clienteEncontrado.numero    && `${clienteEncontrado.numero} - `}
                      {clienteEncontrado.bairro    && `${clienteEncontrado.bairro}`}
                      <br />
                      {clienteEncontrado.cidade    && `${clienteEncontrado.cidade}/`}
                      {clienteEncontrado.uf        && `${clienteEncontrado.uf} - `}
                      {clienteEncontrado.cep       && `CEP: ${clienteEncontrado.cep}`}
                    </ClienteEndereco>
                  )}
                </ClienteCard>
              )}
            </Section>

            {/* Veículo */}
            {requerVeiculo && (
              <Section>
                <SectionTitle><FaCar />Dados do Veículo</SectionTitle>
                <ImportarDocumentoVeiculo onDadosLidos={handleDocumentoVeiculoLido} />
                {veiculoMsg && <HelperText style={{ color: '#27ae60', display: 'block', marginBottom: 8 }}>{veiculoMsg}</HelperText>}
                <VeiculoContainer>
                  <VeiculoFieldGroup>
                    <VeiculoLabel>Placa *</VeiculoLabel>
                    <VeiculoInput type="text" value={placa} onChange={handlePlacaChange} placeholder="AAA-1234" maxLength={8} />
                    <HelperText>Formato: ABC-1234</HelperText>
                  </VeiculoFieldGroup>
                  <VeiculoFieldGroup>
                    <VeiculoLabel>Chassi *</VeiculoLabel>
                    <VeiculoInput type="text" value={chassi} onChange={handleChassiChange} placeholder="9BWZZZ377VT004251" maxLength={17} />
                    <HelperText>17 caracteres (letras e números)</HelperText>
                  </VeiculoFieldGroup>
                </VeiculoContainer>
              </Section>
            )}

            {/* Tipo de Requerimento */}
            <Section>
              <SectionTitle><FaFileAlt />Tipo de Requerimento</SectionTitle>
              <SelectContainer>
                <Select value={tipoRequerimento} onChange={(e) => setTipoRequerimento(e.target.value)}>
                  {tiposRequerimento.map(tipo => (
                    <option key={tipo.id} value={tipo.id}>{tipo.nome}</option>
                  ))}
                </Select>
              </SelectContainer>
            </Section>
          </ModalContent>

          <ModalFooter>
            <CancelButton onClick={onClose}>Cancelar</CancelButton>
            <ConfirmButton onClick={() => handleAbrirRequerimento()}>
              {tipoRequerimento === 'PREENCHIMENTO_ATPVE' ? 'Continuar: escolher adquirente' : 'Continuar para Requerimento'}
            </ConfirmButton>
          </ModalFooter>
        </ModalContainer>
      </ModalOverlay>

      {/* Modal de Edição */}
      {/* Modal do adquirente (só na ATPV-e) */}
      {modalAdquirenteAberto && (
        <SelecionarAdquirenteModal
          vendedor={clienteEncontrado}
          onEscolher={(adquirente, valor) => handleAbrirRequerimento(adquirente, valor)}
          onPular={(valor) => handleAbrirRequerimento(null, valor)}
          onClose={() => setModalAdquirenteAberto(false)}
          API_BASE_URL={API_BASE_URL}
        />
      )}

      {editModalOpen && clienteEmEdicao && (
        <EditClienteModal
          cliente={clienteEmEdicao}
          onSave={handleClienteEditado}
          onClose={() => { setEditModalOpen(false); setClienteEmEdicao(null); }}
          API_BASE_URL={API_BASE_URL}
        />
      )}
    </>
  );
};

// ─── Styled Components ────────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0,0,0,0.6);
  display: flex; justify-content: center; align-items: center;
  z-index: 1000;
  padding: 16px;
  box-sizing: border-box;
  animation: fadeIn 0.2s ease;
  @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
`;

const ModalContainer = styled.div`
  background: white; border-radius: 16px;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow: hidden; display: flex; flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  animation: slideUp 0.3s ease;
  box-sizing: border-box;

  @media (max-width: 600px) {
    max-width: 100%;
    border-radius: 12px;
  }

  @media (max-height: 700px) {
    max-height: 95vh;
  }

  @keyframes slideUp {
    from { transform: translateY(50px); opacity: 0 }
    to   { transform: translateY(0);    opacity: 1 }
  }
`;

const ModalHeader = styled.div`
  background: linear-gradient(135deg, #004d4d 0%, #008080 100%);
  color: white; padding: 20px 24px;
  display: flex; align-items: center; gap: 12px;
  flex-shrink: 0;

  @media (max-width: 480px) {
    padding: 16px 18px;
  }
`;

const ModalTitle = styled.h2`
  margin: 0; font-size: 1.3rem; flex: 1;

  @media (max-width: 480px) {
    font-size: 1.05rem;
  }
`;

const CloseButton = styled.button`
  background: none; border: none; color: white; font-size: 28px;
  cursor: pointer; padding: 0; width: 32px; height: 32px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 8px; transition: background 0.2s;
  flex-shrink: 0;
  &:hover { background: rgba(255,255,255,0.1); }
`;

const ModalContent = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;

  @media (max-width: 480px) {
    padding: 16px;
  }
`;

const Section = styled.div`margin-bottom: 28px;`;

const SectionTitle = styled.h3`
  display: flex; align-items: center; gap: 8px;
  color: #004d4d; font-size: 1rem; margin: 0 0 16px 0;
  padding-bottom: 8px; border-bottom: 2px solid #008080;
`;

const TipoBuscaContainer = styled.div`
  display: flex; gap: 12px; margin-bottom: 16px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const TipoBuscaButton = styled.button`
  flex: 1; padding: 10px 16px;
  background: ${p => p.active ? '#008080' : '#f0f0f0'};
  color: ${p => p.active ? 'white' : '#666'};
  border: none; border-radius: 8px; cursor: pointer;
  font-size: 13px; font-weight: 600;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  transition: all 0.2s;
  &:hover { background: ${p => p.active ? '#008080' : '#e0e0e0'}; transform: translateY(-1px); }
`;

const SearchContainer = styled.div`
  display: flex; gap: 12px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const SearchInput = styled.input`
  flex: 1; padding: 12px 16px;
  border: 2px solid #e0e0e0; border-radius: 8px;
  font-size: 14px; transition: border-color 0.2s;
  min-width: 0;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #008080; }
`;

const SearchButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #004d4d 0%, #008080 100%);
  color: white; border: none; border-radius: 8px;
  cursor: pointer; font-weight: 600;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  transition: transform 0.2s, box-shadow 0.2s;
  flex-shrink: 0;
  &:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,128,128,0.3); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ClientesListContainer = styled.div`
  margin-top: 16px; border: 1px solid #e0e0e0;
  border-radius: 12px; overflow: hidden; background: white;
  max-height: 300px; overflow-y: auto;
`;

const ListTitle = styled.div`
  padding: 12px 16px; background: #f8f9fa; font-weight: 600; color: #004d4d;
  border-bottom: 1px solid #e0e0e0; position: sticky; top: 0;
`;

const ClienteListItem = styled.div`
  display: flex; align-items: center; gap: 12px; padding: 12px 16px;
  transition: background 0.2s; border-bottom: 1px solid #f0f0f0;
  &:hover { background: #f8fdfc; }
  &:last-child { border-bottom: none; }
`;

const ClienteListAvatar = styled.div`
  width: 36px; height: 36px;
  background: linear-gradient(135deg, #008080 0%, #20b2aa 100%);
  border-radius: 50%; display: flex; align-items: center; justify-content: center;
  color: white; flex-shrink: 0;
`;

const ClienteListInfo    = styled.div`flex: 1; min-width: 0;`;
const ClienteListNome    = styled.div`font-weight: 600; color: #004d4d; margin-bottom: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
const ClienteListDetalhes = styled.div`font-size: 11px; color: #666; display: flex; gap: 10px; flex-wrap: wrap; align-items: center;`;

const ClienteListDoc = styled.div`
  display: flex; align-items: center; gap: 5px;
  flex-shrink: 0; font-size: 11px; font-weight: 600;
  color: #008080; background: #e6f7f5;
  border: 1px solid #b8e0dc; border-radius: 6px; padding: 4px 8px; white-space: nowrap;
`;

/* ── NOVO: badge de alerta quando o cliente não tem CPF/CNPJ cadastrado —
    espelha exatamente o padrão usado no modal de busca de OrdemServico.jsx ── */
const ClienteListDocAlerta = styled.div`
  display: flex; align-items: center; gap: 4px;
  flex-shrink: 0; font-size: 10px; font-weight: 600;
  color: #e07030; background: #fff5eb;
  border: 1px solid #ffd8b0; border-radius: 6px; padding: 3px 7px; white-space: nowrap;
`;

/* ── NOVO: ações (editar/selecionar) por item da lista de resultados —
    substitui o onClick no item inteiro, que selecionava direto sem checar
    se o cliente tinha CPF/CNPJ cadastrado ── */
const ClienteListAcoes = styled.div`
  display: flex; gap: 6px; flex-shrink: 0;
`;

const ClienteListBtnEditar = styled.button`
  display: flex; align-items: center; justify-content: center;
  padding: 7px 10px; font-size: 12px;
  color: white;
  background: linear-gradient(135deg, #e07030 0%, #f0a060 100%);
  border: none; border-radius: 7px; cursor: pointer;
  transition: all 0.2s;
  &:hover { transform: translateY(-1px); box-shadow: 0 3px 8px rgba(200,100,0,0.3); }
`;

const ClienteListBtnSelecionar = styled.button`
  display: flex; align-items: center; gap: 5px;
  padding: 7px 12px; font-size: 11px; font-weight: 700;
  color: white;
  background: linear-gradient(135deg, #008080 0%, #20b2aa 100%);
  border: none; border-radius: 7px; cursor: pointer; white-space: nowrap;
  transition: all 0.2s;
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 3px 8px rgba(0,128,128,0.25); }
  &:disabled { background: #bdc3c7; cursor: not-allowed; opacity: 0.85; }
`;

/* ── Banner nenhum resultado ── */
const NaoEncontradoBanner = styled.div`
  margin-top: 16px;
  display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;
  background: #fff8f0; border: 1.5px dashed #e0974d;
  border-radius: 12px; padding: 14px 18px;
`;

const NaoEncontradoTexto = styled.div`display: flex; align-items: center; gap: 14px;`;

const NaoEncontradoIcone = styled.div`
  width: 44px; height: 44px; border-radius: 50%;
  background: linear-gradient(135deg, #f0a060 0%, #e07030 100%);
  display: flex; align-items: center; justify-content: center; color: white; flex-shrink: 0;
`;

const NaoEncontradoTitulo = styled.div`font-weight: 700; color: #7a3e00; font-size: 14px;`;
const NaoEncontradoSubtitulo = styled.div`font-size: 12px; color: #a05010; margin-top: 2px;`;

const CadastrarNovoBtn = styled.button`
  display: flex; align-items: center; gap: 7px;
  padding: 9px 18px; font-size: 13px; font-weight: 700;
  color: white;
  background: linear-gradient(135deg, #e07030 0%, #f0a060 100%);
  border: none; border-radius: 8px; cursor: pointer; white-space: nowrap;
  transition: all 0.2s;
  &:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(200,100,0,0.3); }
`;

/* ── Formulário de novo cadastro inline ── */
const NovoCadastroWrapper = styled.div`
  margin-top: 16px;
  border: 2px solid #008080; border-radius: 12px; overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,128,128,0.12);
`;

const NovoCadastroHeader = styled.div`
  background: linear-gradient(135deg, #005f5f 0%, #009999 100%);
  color: white; padding: 12px 18px;
  display: flex; align-items: center; gap: 9px;
  font-size: 14px; font-weight: 700;
  span { flex: 1; }
`;

const NovoCadastroCloseBtn = styled.button`
  background: none; border: none; color: white; cursor: pointer;
  padding: 4px 6px; border-radius: 6px; display: flex; align-items: center;
  transition: background 0.2s;
  &:hover { background: rgba(255,255,255,0.15); }
`;

const NovoCadastroBody = styled.div`
  padding: 18px 20px; background: #f8ffff; display: flex; flex-direction: column; gap: 12px;

  @media (max-width: 480px) {
    padding: 14px 16px;
  }
`;

const NovoCadastroFooter = styled.div`
  padding: 12px 20px; background: #f0fafa; border-top: 1px solid #c5e0dc;
  display: flex; justify-content: flex-end; gap: 10px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    padding: 12px 16px;
  }
`;

const NCRow = styled.div`
  display: flex; gap: 12px; flex-wrap: wrap;
`;

const NCGroup = styled.div`
  flex: ${p => p.full ? '1 1 100%' : '1 1 140px'};
  display: flex; flex-direction: column; gap: 3px;
  min-width: 0;
`;

const NCLabel = styled.label`
  font-size: 11px; font-weight: 700; color: #004d4d;
  text-transform: uppercase; letter-spacing: 0.4px;
  display: flex; align-items: center; gap: 4px;
`;

const NCInput = styled.input`
  width: 100%; padding: 8px 11px; border: 2px solid #dde8e8; border-radius: 7px;
  font-size: 13px; color: #222; transition: all 0.2s; box-sizing: border-box;
  &:focus { outline: none; border-color: #008080; box-shadow: 0 0 0 2px rgba(0,128,128,0.1); }
`;

const NCInputRelative = styled.div`position: relative;`;

const NCStatusIcon = styled.span`
  position: absolute; right: 11px; top: 50%; transform: translateY(-50%);
  font-size: 15px; display: flex; align-items: center;
`;

const NCHint  = styled.span`font-size: 10px; color: #6b7e90;`;
const NCError = styled.span`font-size: 11px; color: #e74c3c; font-weight: 600;`;

const NCSectionLabel = styled.div`
  font-size: 11px; font-weight: 700; color: #008080;
  text-transform: uppercase; letter-spacing: 0.5px;
  padding-bottom: 5px; border-bottom: 1.5px dashed #b8e0dc;
  display: flex; align-items: center; gap: 5px;
`;

/* ── Cliente card ── */
const ClienteCard = styled.div`
  margin-top: 16px; background: #f0f9f8;
  border: 1px solid #b8e0dc; border-radius: 12px; padding: 16px;
`;

const ClienteHeader = styled.div`
  display: flex; gap: 12px; margin-bottom: 12px;

  @media (max-width: 480px) {
    flex-wrap: wrap;
  }
`;

const ClienteAvatar = styled.div`
  width: 48px; height: 48px;
  background: linear-gradient(135deg, #008080 0%, #20b2aa 100%);
  border-radius: 50%; display: flex; align-items: center; justify-content: center;
  color: white; flex-shrink: 0;
`;

const ClienteInfo = styled.div`flex: 1; min-width: 0;`;

const ClienteNomeRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 6px; gap: 8px; flex-wrap: wrap;
`;

const ClienteNome = styled.h4`margin: 0; color: #004d4d; font-size: 1rem; word-break: break-word;`;

const EditarBtn = styled.button`
  display: flex; align-items: center; gap: 5px;
  padding: 4px 10px; font-size: 11px; font-weight: 600;
  color: #008080; background: transparent;
  border: 1.5px solid #008080; border-radius: 6px;
  cursor: pointer; white-space: nowrap; transition: all 0.2s;
  flex-shrink: 0;
  &:hover {
    background: #008080; color: white;
    transform: translateY(-1px); box-shadow: 0 3px 8px rgba(0,128,128,0.25);
  }
`;

const ClienteDetalhes = styled.div`
  display: flex; flex-wrap: wrap; gap: 12px;
  font-size: 12px; color: #666;
  span { display: flex; align-items: center; gap: 4px; }
`;

const ClienteEndereco = styled.div`
  font-size: 12px; color: #666; padding-top: 8px;
  border-top: 1px solid #c5e0dc; line-height: 1.5;
`;

/* ── Veículo ── */
const VeiculoContainer = styled.div`
  display: flex; gap: 20px; flex-wrap: wrap;
  @media (max-width: 600px) { flex-direction: column; gap: 16px; }
`;

const VeiculoFieldGroup = styled.div`flex: 1; display: flex; flex-direction: column; gap: 6px; min-width: 0;`;
const VeiculoLabel = styled.label`font-size: 12px; font-weight: 600; color: #004d4d;`;

const VeiculoInput = styled.input`
  padding: 10px 12px; border: 2px solid #e0e0e0; border-radius: 8px;
  font-size: 13px; text-transform: uppercase; transition: all 0.2s;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #008080; box-shadow: 0 0 0 2px rgba(0,128,128,0.1); }
  &::placeholder { text-transform: none; color: #bbb; font-size: 12px; }
`;

const HelperText    = styled.span`font-size: 10px; color: #6b7e90; margin-top: 2px;`;
const SelectContainer = styled.div``;

const Select = styled.select`
  width: 100%; padding: 12px 16px; border: 2px solid #e0e0e0;
  border-radius: 8px; font-size: 13px; background: white;
  cursor: pointer; font-weight: 500;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #008080; }
`;

/* ── Footer modal principal ── */
const ModalFooter = styled.div`
  padding: 16px 24px; border-top: 1px solid #e0e0e0;
  display: flex; justify-content: flex-end; gap: 12px; background: #fafafa;
  flex-shrink: 0;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    padding: 14px 16px;
  }
`;

const CancelButton = styled.button`
  padding: 10px 20px; background: #f0f0f0; border: none;
  border-radius: 8px; cursor: pointer; font-weight: 600; color: #666;
  transition: background 0.2s;
  &:hover { background: #e0e0e0; }
`;

const ConfirmButton = styled.button`
  padding: 10px 24px;
  background: linear-gradient(135deg, #004d4d 0%, #008080 100%);
  color: white; border: none; border-radius: 8px;
  cursor: pointer; font-weight: 600; transition: transform 0.2s, box-shadow 0.2s;
  &:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,128,128,0.3); }
`;

/* ── Edit Modal Styles ────────────────────────────────────────────────────
   Mesma estratégia de responsividade aplicada ao modal de edição:
   max-height em vh + media queries para zoom alto / telas pequenas. ── */
const EditOverlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0,0,0,0.55);
  display: flex; justify-content: center; align-items: center;
  z-index: 1100;
  padding: 16px;
  box-sizing: border-box;
  animation: fadeIn 0.15s ease;
  @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
`;

const EditContainer = styled.div`
  background: white; border-radius: 16px;
  width: 100%;
  max-width: 680px;
  max-height: 88vh;
  overflow: hidden; display: flex; flex-direction: column;
  box-shadow: 0 24px 70px rgba(0,0,0,0.35);
  animation: slideUp 0.25s ease;
  box-sizing: border-box;

  @media (max-width: 600px) {
    max-width: 100%;
    border-radius: 12px;
  }

  @media (max-height: 700px) {
    max-height: 94vh;
  }

  @keyframes slideUp {
    from { transform: translateY(40px); opacity: 0 }
    to   { transform: translateY(0);    opacity: 1 }
  }
`;

const EditHeader = styled.div`
  background: linear-gradient(135deg, #005f5f 0%, #009999 100%);
  color: white; padding: 16px 22px;
  display: flex; align-items: center; gap: 10px;
  font-size: 1rem; font-weight: 700;
  flex-shrink: 0;
  span { flex: 1; }

  @media (max-width: 480px) {
    padding: 14px 16px;
  }
`;

const EditCloseBtn = styled.button`
  background: none; border: none; color: white;
  font-size: 16px; cursor: pointer; padding: 4px 6px;
  border-radius: 6px; display: flex; align-items: center;
  transition: background 0.2s;
  flex-shrink: 0;
  &:hover { background: rgba(255,255,255,0.15); }
`;

const EditBody = styled.div`
  padding: 20px 22px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;

  @media (max-width: 480px) {
    padding: 14px 16px;
  }
`;

const EditRow = styled.div`
  display: flex; gap: 14px; margin-bottom: 14px; flex-wrap: wrap;
`;

const EditGroup = styled.div`
  flex: ${p => p.full ? '1 1 100%' : '1 1 140px'};
  display: flex; flex-direction: column; gap: 4px;
  min-width: 0;
`;

const EditLabel = styled.label`font-size: 11px; font-weight: 700; color: #004d4d; text-transform: uppercase; letter-spacing: 0.4px;`;

const EditInput = styled.input`
  padding: 9px 12px; border: 2px solid #dde8e8; border-radius: 8px;
  font-size: 13px; color: #222; transition: all 0.2s;
  width: 100%;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #008080; box-shadow: 0 0 0 2px rgba(0,128,128,0.1); }
`;

/* ── CORREÇÃO: hint de carregamento do CEP, no mesmo padrão do NCHint ── */
const EditHint = styled.span`font-size: 10px; color: #6b7e90;`;

const EditSectionLabel = styled.div`
  font-size: 11px; font-weight: 700; color: #008080;
  text-transform: uppercase; letter-spacing: 0.5px;
  margin-bottom: 10px; padding-bottom: 6px;
  border-bottom: 1.5px dashed #b8e0dc;
`;

const EditFooter = styled.div`
  padding: 14px 22px; border-top: 1px solid #e0e0e0;
  display: flex; justify-content: flex-end; gap: 12px; background: #fafafa;
  flex-shrink: 0;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    padding: 12px 16px;
  }
`;

const EditCancelBtn = styled.button`
  padding: 9px 18px; background: #f0f0f0; border: none;
  border-radius: 8px; cursor: pointer; font-weight: 600; color: #666;
  transition: background 0.2s;
  white-space: nowrap;
  font-size: 13px;
  &:hover { background: #e0e0e0; }

  @media (max-width: 480px) {
    width: 100%;
    padding: 11px 16px;
  }
`;

const EditSaveBtn = styled.button`
  padding: 9px 22px;
  background: linear-gradient(135deg, #004d4d 0%, #008080 100%);
  color: white; border: none; border-radius: 8px;
  cursor: pointer; font-weight: 600;
  display: flex; align-items: center; justify-content: center; gap: 7px;
  transition: transform 0.2s, box-shadow 0.2s;
  white-space: nowrap;
  font-size: 13px;
  &:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,128,128,0.3); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }

  @media (max-width: 480px) {
    width: 100%;
    padding: 11px 16px;
  }
`;

export default RequerimentoDetranModal;
